import { ServerPagingListTable, TextBody3, TextLabel } from "common/components";
import DateField from "common/components/Date/DateField";
import { ComboBox } from "common/components/Field/new/ComboBox";
import { SearchInput } from "common/components/Input/SearchInput";
import {
  Column,
  DefaultLayout,
  Row,
} from "common/components/Layout/new/constLayout";
import { DefaultModal } from "common/components/Modal/DefaultTextModal";
import {
  closeGlobalModal,
  openGlobalModal,
  showMessageModal,
} from "common/utils/modalController";
import { useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { SalesDetailModalBody } from "./SalesDetailModalBody";
import { apiRequest, useApiRequest } from "common/utils/api";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { formatDateTime, parseDate } from "common/utils/dateUtil";
import {
  DiscountFilterType,
  DiscountType,
  PaymentCategory,
  PaymentChannel,
  PaymentMethod,
  PaymentStatus,
} from "common/enum/PaymentTypes";
import { DefaultButton } from "common/components/Button/new/DefaultButton";
import { colors } from "common/const/colors";
import { I18N_KEYS } from "common/i18n/keys";
import { useTranslation } from "react-i18next";
import { DateInput } from "common/components/Input/DateInput";
import { ServerPagedList } from "common/components/List/new/PagedList/ServerPagedList";
import { SALES_LIST_COLUMNS } from "common/data/columns/salesListColumns";

export const SalesDetailListScreen = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useState({
    clubId: sessionStorage.getItem("clubId"),
    pageNum: 1,
  });

  const today = new Date();
  const isRefundingRef = useRef(false);
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      startDate: formatDateTime(
        (() => {
          const d = new Date();
          d.setMonth(d.getMonth() - 1);
          return d;
        })(),
      ),
      endDate: formatDateTime(new Date()),
    },
  });

  const { data, loading, refetch } = useApiRequest({
    method: REQUEST_METHOD.GET,
    url: `/api/v2.0/payment`,
    queryParameter: searchParams,
  });

  const salesDataList = data?.payments?.list;
  const totalCnt = data?.payments?.count ?? 0;
  const pageSize = 10;

  const openDetailModal = (id) => {
    openGlobalModal({
      content: (
        <DefaultModal
          title={"Sales Details"}
          fixedWidth={"40rem"}
          fixedHeight={"41.5rem"}
          footer={
            <>
              <DefaultButton.White
                outlineColor={colors.MainBlue}
                text={t(I18N_KEYS.common.refund)}
                onClick={async () => {
                  if (isRefundingRef.current) return;
                  isRefundingRef.current = true;
                  try {
                    await apiRequest({
                      method: REQUEST_METHOD.PATCH,
                      url: `/api/v2.0/payment/${id}/cancel`,
                      body: {
                        clubId: sessionStorage.getItem("clubId"),
                      },
                    });
                    closeGlobalModal();
                  } catch (e) {
                    closeGlobalModal();
                    showMessageModal(e.message);
                  } finally {
                    isRefundingRef.current = false;
                    refetch();
                  }
                }}
              />
              <DefaultButton.White
                outlineColor={colors.LightNavy}
                text={t(I18N_KEYS.common.close)}
                onClick={() => closeGlobalModal()}
              />
            </>
          }>
          <SalesDetailModalBody paymentId={id} />
        </DefaultModal>
      ),
    });
  };

  const handlePageChange = (pageId) => {
    const newParams = { ...searchParams, pageNum: pageId };
    setSearchParams(newParams);
    refetch({ overrideParams: newParams });
  };

  return (
    <DefaultLayout title={t(I18N_KEYS.sales.detail.title)}>
      <FormProvider {...methods}>
        <Column style={{ alignItems: "flex-end" }}>
          <SearchInput
            methods={methods}
            placeholder={t(I18N_KEYS.sales.detail.filter.search.placeholder)}
            onReset={() => {
              methods.reset();
            }}
            onSearch={async () => {
              const param = methods.getValues();
              const filteredParam = Object.fromEntries(
                Object.entries(param).filter(
                  ([_, val]) => val !== "ALL" && val !== "" && val != null,
                ),
              );
              const newParams = {
                clubId: sessionStorage.getItem("clubId"),
                pageNum: 1,
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
                ...filteredParam,
              };
              setSearchParams(newParams);
              refetch({ overrideParams: newParams });
            }}>
            <DateInput
              name={"startDate"}
              label={t(I18N_KEYS.component.tableFilter.date)}
              placeholder={"Start"}
            />
            <DateInput name={"endDate"} placeholder={"End"} />
          </SearchInput>
          <Row>
            <ComboBox
              name={"searchCategory"}
              label={t(I18N_KEYS.sales.detail.filter.category)}
              placeholder={t(I18N_KEYS.common.all)}
              itemList={Object.entries(PaymentCategory).map(([key, val]) => ({
                text: t(val),
                value: key,
              }))}
            />
            <ComboBox
              name={"searchChannel"}
              label={t(I18N_KEYS.sales.detail.filter.channel)}
              placeholder={t(I18N_KEYS.common.all)}
              itemList={Object.entries(PaymentChannel).map(([key, val]) => ({
                text: t(val),
                value: key,
              }))}
            />
            <ComboBox
              name={"searchMethod"}
              label={t(I18N_KEYS.sales.detail.filter.method)}
              placeholder={t(I18N_KEYS.common.all)}
              itemList={Object.entries(PaymentMethod).map(([key, val]) => ({
                text: t(val),
                value: key,
              }))}
            />
            <ComboBox
              name={"searchDiscount"}
              label={t(I18N_KEYS.sales.detail.filter.discount)}
              placeholder={t(I18N_KEYS.common.all)}
              itemList={Object.entries(DiscountFilterType).map(
                ([key, val]) => ({
                  text: t(val),
                  value: key,
                }),
              )}
            />
          </Row>
        </Column>
        {console.log(salesDataList)}
        <ServerPagedList
          data={salesDataList ?? []}
          currentPage={searchParams.pageNum}
          totalPages={Math.ceil(totalCnt / pageSize)}
          onPageChange={handlePageChange}
          columns={SALES_LIST_COLUMNS}
          renderProps={{ t, openDetailModal }}
        />
        {/* <ServerPagingListTable
          isLoading={loading}
          titleList={defaultTitleList}
          dataList={
            salesDataList && salesDataList.length > 0
              ? salesDataList.map((item) => [
                  item.paymentAt?.replace("T", " ").slice(0, 19),
                  <EditLink
                    onClick={() =>
                      openGlobalModal({
                        content: (
                          <DefaultModal
                            title={"Sales Details"}
                            fixedWidth={"40rem"}
                            fixedHeight={"41.5rem"}
                            footer={
                              <>
                                <DefaultButton.White
                                  outlineColor={colors.MainBlue}
                                  text={t(I18N_KEYS.common.refund)}
                                  onClick={async () => {
                                    if (isRefundingRef.current) return;
                                    isRefundingRef.current = true;
                                    try {
                                      await apiRequest({
                                        method: REQUEST_METHOD.PATCH,
                                        url: `/api/v2.0/payment/${item.id}/cancel`,
                                        body: {
                                          clubId:
                                            sessionStorage.getItem("clubId"),
                                        },
                                      });
                                      closeGlobalModal();
                                    } catch (e) {
                                      closeGlobalModal();
                                      showMessageModal(e.message);
                                    } finally {
                                      isRefundingRef.current = false;
                                      refetch();
                                    }
                                  }}
                                />
                                <DefaultButton.White
                                  outlineColor={colors.LightNavy}
                                  text={t(I18N_KEYS.common.close)}
                                  onClick={() => closeGlobalModal()}
                                />
                              </>
                            }>
                            <SalesDetailModalBody paymentId={item.id} />
                          </DefaultModal>
                        ),
                      })
                    }>
                    {item.paymentTrxId}
                  </EditLink>,
                  PaymentCategory[item.category],
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "center",
                    }}>
                    {item?.items[0]?.itemName ?? "-"}
                    {item?.items.length > 1 && (
                      <TextBody3>+{item?.items.length - 1}</TextBody3>
                    )}
                  </span>,
                  item?.items?.reduce((acc, i) => acc + i.quantity, 0) ?? "-",
                  `${item.currency} ${Number(item.totalAmount)}`,
                  `${item.currency} ${Number(item.actualAmount)}`,
                  item.discounts
                    ?.map((d) => DiscountType[d.discountType])
                    .join("+") || "-",
                  PaymentMethod[item.paymentMethod],
                  <TextLabel color={PaymentStatus[item.status]?.color}>
                    {PaymentStatus[item.status]?.text}
                  </TextLabel>,
                  PaymentChannel[item.paymentPath],
                ])
              : []
          }
          emptyMessage={"No data"}
          widthList={defaultWidthList}
          externalCurrentPage={1}
          onePageCnt={10}
          totalCnt={totalCnt}
          listFunction={async (pageId) => {
            const newParams = { ...searchParams, pageNum: pageId };
            setSearchParams(newParams);
            refetch({ overrideParams: newParams });
          }}></ServerPagingListTable> */}
      </FormProvider>
    </DefaultLayout>
  );
};

const defaultWidthList = [
  "110px",
  "220px",
  "100px",
  "120px",
  "60px",
  "100px",
  "100px",
  "100px",
  "100px",
  "100px",
  "100px",
];

const EditLink = styled(Link)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 24px;
`;
